import React, { useState } from 'react';
import { ACCES_OPTIONS, SOL_OPTIONS } from '../../calculations/cuveCalculations.js';
import RefinePanel from './RefinePanel.js';

const TYPE_OPTIONS = [
  { val: 'aerienne', label: 'Cuve aérienne', desc: 'Plastique ou résine, installation simple, en extérieur ou abri', price: 'Prix au m³ dégressif selon le volume (voir barème)' },
  { val: 'enterree', label: 'Cuve enterrée', desc: 'Béton ou PEHD, installation par terrassement, invisible', price: 'Prix au m³ incluant le terrassement (voir barème)' },
];

const MAINOEUVRE_OPTIONS = [
  { val: 'diy', label: 'Auto-installation', desc: 'Vous réalisez la pose vous-même', price: "0 € de main-d'œuvre" },
  { val: 'pro', label: 'Installateur professionnel', desc: 'Positionnement, raccords entrée/sortie, étanchéité', price: '600 – 1 200 € selon complexité' },
];

/**
 * Section Cuve : type, volume, main-d'œuvre de pose — affinable avec accès chantier / nature
 * du sol / distance (cuve enterrée uniquement, terrassement en plus de la pose ci-dessus).
 */
const CuveSection = ({ cuve, setCuve }) => {
  const [baremeOpen, setBaremeOpen] = useState(false);

  return (
    <div>
      <p className="apv2-sim-hint">Volume pré-rempli avec la cuve choisie à l'étape 5 (convertie en litres et arrondie au pas du curseur) — ajustable ici librement.</p>

      <div className="rw-sub-label">Type de cuve</div>
      <div className="rw-budget-grid rw-budget-grid--2">
        {TYPE_OPTIONS.map(opt => (
          <div
            key={opt.val}
            className={`rw-budget-card ${cuve.type === opt.val ? 'rw-budget-card--selected' : ''}`}
            onClick={() => setCuve({ type: opt.val })}
          >
            <div className="rw-budget-card-label">{opt.label}</div>
            <div className="rw-budget-card-desc">{opt.desc}</div>
            <div className="rw-budget-card-price">{opt.price}</div>
          </div>
        ))}
      </div>

      <div className="rw-sub-label">Volume de la cuve</div>
      <div className="rw-slider-wrap">
        <div className="rw-slider-row">
          <input
            type="range" min="500" max="100000" step="500"
            value={cuve.volume}
            onChange={e => setCuve({ volume: +e.target.value })}
          />
          <span className="rw-slider-val">{cuve.volume.toLocaleString('fr-FR')} L</span>
        </div>
        <div className="rw-slider-bounds">
          <span>500 L <i>(récupération basique)</i></span>
          <span>100 000 L <i>(gros collectif)</i></span>
        </div>
      </div>

      <RefinePanel open={baremeOpen} onToggle={() => setBaremeOpen(o => !o)} label="Voir le barème détaillé">
        <p className="apv2-sim-hint" style={{ margin: 0 }}>
          Barème (€/m³ stocké, HT) — <b>Enterré</b> : 3–10 m³ 1000–1200 · 11–20 m³ 800–1000 · 21–30 m³ 700–900 ·
          31–50 m³ 650–850 · +50 m³ 650–850. <b>Aérien</b> : 3–10 m³ 500–700 · 11–20 m³ 300–500 · 21–30 m³ 200–400 ·
          31–50 m³ 180–360 · +50 m³ 160–340. En dessous de 3 m³, le tarif de la tranche 3–10 m³ est appliqué par extrapolation.
        </p>
      </RefinePanel>

      <div className="rw-sub-label">Main-d'œuvre — installation de la cuve</div>
      <div className="rw-budget-grid rw-budget-grid--2">
        {MAINOEUVRE_OPTIONS.map(opt => (
          <div
            key={opt.val}
            className={`rw-budget-card ${cuve.mainoeuvre === opt.val ? 'rw-budget-card--selected' : ''}`}
            onClick={() => setCuve({ mainoeuvre: opt.val })}
          >
            <div className="rw-budget-card-label">{opt.label}</div>
            <div className="rw-budget-card-desc">{opt.desc}</div>
            <div className="rw-budget-card-price">{opt.price}</div>
          </div>
        ))}
      </div>

      {cuve.type === 'enterree' && (
        <RefinePanel open={cuve.refined} onToggle={() => setCuve({ refined: !cuve.refined })}>
          <p className="apv2-sim-hint" style={{ marginTop: 0 }}>
            Terrassement (excavation, remblai) — poste distinct de la main-d'œuvre de pose ci-dessus.
          </p>
          <div className="apv2-filter-panel-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 14 }}>
            <div>
              <span className="apv2-filter-panel-label" style={{ display: 'block', marginBottom: 8 }}>Accès chantier</span>
              <div className="rw-budget-grid rw-budget-grid--3">
                {ACCES_OPTIONS.map(opt => (
                  <div
                    key={opt.val}
                    className={`rw-budget-card ${cuve.acces === opt.val ? 'rw-budget-card--selected' : ''}`}
                    onClick={() => setCuve({ acces: opt.val })}
                  >
                    <div className="rw-budget-card-label">{opt.label}</div>
                    <div className="rw-budget-card-desc">{opt.desc}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <span className="apv2-filter-panel-label" style={{ display: 'block', marginBottom: 8 }}>Nature du sol</span>
              <div className="rw-budget-grid rw-budget-grid--3">
                {SOL_OPTIONS.map(opt => (
                  <div
                    key={opt.val}
                    className={`rw-budget-card ${cuve.sol === opt.val ? 'rw-budget-card--selected' : ''}`}
                    onClick={() => setCuve({ sol: opt.val })}
                  >
                    <div className="rw-budget-card-label">{opt.label}</div>
                    <div className="rw-budget-card-desc">{opt.desc}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rw-param-field">
              <label>Distance cuve → local technique (ml)</label>
              <input
                type="number" min="0" step="1"
                value={cuve.distanceMaison || 0}
                onChange={e => setCuve({ distanceMaison: +e.target.value || 0 })}
              />
            </div>
          </div>
        </RefinePanel>
      )}
    </div>
  );
};

export default CuveSection;
